import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const SearchableDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Search...',
  disableSort = false,
  error = false,
  helperText = '',
  ...rest
}) => {


  const sortedOptions = React.useMemo(() => {
    if (disableSort) return options;
    return [...options].sort((a, b) => {
      const labelA = (a.label || '').toLowerCase();
      const labelB = (b.label || '').toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [options, disableSort]);
  const [themeColor, setThemeColor] = useState('#000');
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  useEffect(() => {
    const getThemeColor = () => {
      try {
        const settings = localStorage.getItem('settings');
        
        // If no settings in localStorage, return default light theme colors
        if (!settings) {
          setIsDarkTheme(false);
          return '#000';
        }
        
        const parsedSettings = JSON.parse(settings);
        const mainTheme = parsedSettings?.theme?.main;
        
        if (mainTheme === 'defaultDark') {
          setIsDarkTheme(true);
          return '#fff'; 
        } else {
          setIsDarkTheme(false);
          return '#000'; 
        }
      } catch (err) {
        console.error('Error parsing localStorage settings:', err);
        // On any error, default to light theme
        setIsDarkTheme(false);
        return '#000';
      }
    };

    setThemeColor(getThemeColor());

    const intervalId = setInterval(() => {
      const currentColor = getThemeColor();
      setThemeColor(prev => {
        if (prev !== currentColor) {
          return currentColor;
        }
        return prev;
      });
    }, 500);

    const handleStorageChange = (e) => {
      if (e.key === 'settings') {
        setThemeColor(getThemeColor());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Autocomplete
      options={sortedOptions}
      getOptionLabel={(option) => option.label || ''}
      value={value}
      size="small"
      onChange={(event, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          error={error}
          helperText={helperText}
          {...rest}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '5px',
              fontSize: '14px',
              paddingY: '2px',
              backgroundColor: isDarkTheme ? 'transparent' : '#FAFAFA',
              '& fieldset': {
                borderColor: themeColor,
              },
              '&:hover fieldset': {
                borderColor: themeColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: themeColor,
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
              color: themeColor,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: themeColor,
            },
            '& .MuiOutlinedInput-input': {
              color: themeColor,
            },
            '& .MuiFormHelperText-root': {
              fontSize: '13px',
            },
          }}
        />
      )}
      fullWidth
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 6],
              },
            },
          ],
          sx: {
            '& .MuiPaper-root': {
              backgroundColor: isDarkTheme ? '#1D1F1F' : '#fff',
            },
            '& .MuiAutocomplete-listbox': {
              fontSize: '14px',
              paddingY: 1,
              backgroundColor: isDarkTheme ? '#1D1F1F' : '#fff',
            },
            '& .MuiAutocomplete-option': {
              fontSize: '12px',
              paddingY: 1,
              paddingX: 1.2,
              minHeight: '40px',
              color: themeColor,
              borderBottom: isDarkTheme ? '1px solid #333' : '1px solid #f0f0f0',
              '&:hover': {
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#f5f5f5',
              },
              '&.Mui-focused': {
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#f5f5f5',
              },
            },
          },
        },
      }}
      {...rest}
    />
  );
};

export default SearchableDropdown;